import { isArray } from '@cutwise/utils';

import { TopicResourceType } from '@/dmdrive/topic/entities/Topic';

import carbonViewerWASM from '@carbon/carbon-viewer/carbonRendererApi.wasm?url';
import { CarbonViewer, CarbonViewerConstructor, PointerData } from '@carbon/carbon-viewer/lib/carbon-viewer';
import {
  DmIndustrial,
  DmIndustrialConstructor,
  FlatRemark,
  InteractionPoint,
} from '@carbon/dm-industrial/lib/dm-industrial';
import imageViewerWASM from '@carbon/image-viewer/imageRendererApi.wasm?url';
import { ImageViewer, ImageViewerConstructor } from '@carbon/image-viewer/lib/image-viewer';
import { Lod3DViewer, Lod3DViewerConstructor } from '@carbon/lod3d-viewer/lib/lod3d-viewer';
import lod3DViewerWASM from '@carbon/lod3d-viewer/lodRendererApi.wasm?url';

type ViewerType = DmIndustrial | ImageViewer | Lod3DViewer | CarbonViewer;

type ViewerConstructorMap = {
  [TopicResourceType.DMIndustrial]: Omit<DmIndustrialConstructor, 'wasmFileURI'>;
  [TopicResourceType.ImageViewer]: Omit<ImageViewerConstructor, 'wasmFileURI'>;
  [TopicResourceType.LODViewer]: Omit<Lod3DViewerConstructor, 'wasmFileURI'>;
  [TopicResourceType.CarbonViewer]: Omit<CarbonViewerConstructor, 'wasmFileURI'>;
};

export class ViewerService {
  private type: TopicResourceType;
  private viewer: Nullable<ViewerType> = null;

  constructor(type: TopicResourceType) {
    this.type = type;
  }

  get fullConfiguration() {
    return this.viewer?.fullConfiguration ?? {};
  }

  get pointersData() {
    return this.viewer?.pointersData ?? [];
  }

  get version() {
    return this.viewer?.packageVersion ?? [];
  }

  create(options: ViewerConstructorMap[TopicResourceType]): void {
    switch (this.type) {
      case TopicResourceType.ImageViewer:
        this.viewer = new ImageViewer({
          wasmFileURI: imageViewerWASM,
          ...options,
        });

        break;
      case TopicResourceType.LODViewer:
        this.viewer = new Lod3DViewer({
          wasmFileURI: lod3DViewerWASM,
          ...options,
        });

        break;
      case TopicResourceType.CarbonViewer:
        this.viewer = new CarbonViewer({
          customPath: [
            {
              key: 'carbonRendererApi.wasm',
              path: carbonViewerWASM,
            },
          ],
          ...options,
        });

        break;
      case TopicResourceType.DMIndustrial:
        this.viewer = new DmIndustrial({
          // keep in mind - that resources for dm-industrial are copied via vite.config.ts
          prefixPath: '/assets/dm-industrial',
          ...options,
        } as ViewerConstructorMap[TopicResourceType.DMIndustrial]);

        break;
      default:
        break;
    }
  }

  setPointers(data: PointerData[]): void {
    if (this.viewer && isArray(data)) {
      this.viewer.pointersData = data;
    }
  }

  setCameraState(camera: string): void {
    this.viewer?.setCameraState(camera);
  }

  fitCameraToScene() {
    // type is not important here, because all viewers support this method
    (this.viewer as Any)?.fitCameraToScene();
  }

  setRemarksVisible(state: boolean) {
    if (this.viewer instanceof DmIndustrial) {
      this.viewer?.setRemarksVisible?.(state);
    }
  }

  putRemarks(remarks: FlatRemark[]) {
    // Only "DmIndustrial" supports
    if (!this.viewer || this.viewer instanceof ImageViewer) {
      return Promise.resolve();
    }

    return this.viewer?.putRemarks(remarks);
  }

  putText(point: InteractionPoint, text: string) {
    // Only "DmIndustrial" supports
    if (this.viewer instanceof DmIndustrial) {
      return this.viewer?.putText(point, text);
    }
  }

  showMaxDistance() {
    // Only "DmIndustrial" supports
    if (this.viewer instanceof DmIndustrial) {
      this.viewer?.showMaxDistance?.();
    }
  }

  removeMaxDistance() {
    // Only "DmIndustrial" supports
    if (this.viewer instanceof DmIndustrial) {
      this.viewer?.removeMaxDistance?.();
    }
  }

  startRecognition() {
    // Only "DmIndustrial" supports
    if (this.viewer instanceof DmIndustrial) {
      this.viewer?.startRecognition?.();
    }
  }

  stopRecognition() {
    // Only "DmIndustrial" supports
    if (this.viewer instanceof DmIndustrial) {
      this.viewer?.stopRecognition?.();
    }
  }

  resetMovableModel() {
    // Only "DmIndustrial" supports
    if (this.viewer instanceof DmIndustrial) {
      this.viewer?.resetMovableModel?.();
    }
  }

  setNonSymmMetricRecognition(state: boolean) {
    // Only "DmIndustrial" supports
    if (this.viewer instanceof DmIndustrial) {
      this.viewer?.setNonSymmMetricRecognition?.(state);
    }
  }

  setSourceMovable() {
    // Only "DmIndustrial" supports
    if (this.viewer instanceof DmIndustrial) {
      this.viewer?.setSourceMovable?.();
    }
  }

  setTargetMovable() {
    // Only "DmIndustrial" supports
    if (this.viewer instanceof DmIndustrial) {
      this.viewer?.setTargetMovable?.();
    }
  }

  apply(options: Record<string, unknown>) {
    this.viewer?.apply(options);
  }

  getScreenshot(filename?: string) {
    // type is not important here, because all viewers support this method
    return (this.viewer as Any)?.getScreenshot(filename);
  }

  dispose() {
    this.viewer?.dispose();
    this.viewer = null;
  }
}
